/**
 * CurrentPlanCard Component
 *
 * Displays the user's current tier/plan information.
 * Part of the "My Access" page for all users.
 */

import { Check, Star } from '@mui/icons-material';
import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import type { ActiveBundleResponse } from '@store/generated/subscriptions';

interface CurrentPlanCardProps {
  tier: ActiveBundleResponse | undefined;
  isLoading?: boolean;
}

export function CurrentPlanCard({ tier, isLoading }: CurrentPlanCardProps) {
  if (isLoading) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular"  sx={{ width: 48, height: 48 }}/>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text"  sx={{ width: "40%", height: 32 }}/>
              <Skeleton variant="text"  sx={{ width: "60%", height: 24 }}/>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!tier) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Star sx={{ color: 'grey.400' }} />
            </Box>
            <Box>
              <Typography variant="h6" color="text.secondary">
                No Plan Selected
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You don&apos;t have an active plan at this time.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={2}
      sx={{
        background: theme =>
          `linear-gradient(135deg, ${theme.palette.success.main}10 0%, ${theme.palette.background.paper} 100%)`,
        border: theme => `1px solid ${theme.palette.success.main}`,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'success.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Check sx={{ color: 'background.paper' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {tier.bundle_name}
            </Typography>
            {tier.bundle_description && (
              <Typography variant="body2" color="text.secondary">
                {tier.bundle_description}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
